import { useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { usePlansCatalog } from '@/context/PlansContext';
import { clientInitials, findClient } from '@/content/clients';
import { formatAppPrice, type PublicApp } from '@/content/apps';
import { effectivePlanPriceCents, formatPlanChannels, formatPlanPrice, formatPlanSeats } from '@/content/plans';
import { solutions, type Solution } from '@/content/solutions';
import { buyablePlans, calculatorCheckoutHref } from '@/lib/calculator';
import { ClientLogo } from '@/pages/site/home/ClientsStrip';

const CATEGORY_GROUPS: { id: string; label: string; cats: string[] }[] = [
  { id: 'operation', label: 'Operação', cats: ['operation'] },
  { id: 'health', label: 'Saúde', cats: ['health'] },
  { id: 'finance', label: 'Financeiro e fiscal', cats: ['finance', 'fiscal'] },
  { id: 'automation', label: 'IA e automação', cats: ['automation'] },
  { id: 'channel', label: 'Canais', cats: ['channel'] },
  { id: 'relationship', label: 'Relacionamento', cats: ['relationship'] },
  { id: 'semantic', label: 'Linguagem do setor', cats: ['semantic'] },
];

const norm = (s: string) => s.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();

const isPaid = (a: PublicApp) => a.tier === 'addon' && (a.pricing?.monthly_price_cents ?? 0) > 0;
const isSoon = (a: PublicApp) => a.availability === 'coming_soon' || a.tier === 'soon';
const isFree = (a: PublicApp) => !isSoon(a) && !isPaid(a);

function AppCard({ app, on, toggle }: { app: PublicApp; on: boolean; toggle: () => void }) {
  const soon = isSoon(app);
  const free = isFree(app);
  return (
    <article className={`hx-app${on ? ' on' : ''}`}>
      <header>
        <h4>{app.name}</h4>
        {soon ? (
          <span className="hx-tag soon">Em breve</span>
        ) : free ? (
          <span className="hx-tag free">Sem custo</span>
        ) : (
          <span className="hx-tag paid">{formatAppPrice(app.pricing!.monthly_price_cents)}/mês</span>
        )}
      </header>
      <p>{app.description}</p>
      {soon ? (
        <button type="button" className="hx-add" disabled>
          Por convite
        </button>
      ) : (
        <button
          type="button"
          className={`hx-add${on ? ' on' : free ? ' free' : ''}`}
          aria-pressed={on}
          onClick={toggle}
        >
          {on ? '✓ No seu plano' : free ? '+ Ativar sem custo' : '+ Adicionar'}
        </button>
      )}
    </article>
  );
}

function SolutionPanel({
  sol,
  active,
  bySlug,
  onAdd,
}: {
  sol: Solution;
  active: boolean;
  bySlug: Map<string, PublicApp>;
  onAdd: (slugs: string[]) => void;
}) {
  const solApps = sol.apps.map((s) => bySlug.get(s)).filter((a): a is PublicApp => Boolean(a));
  const solPaid = solApps.filter(isPaid);
  const solFree = solApps.filter(isFree);
  const solTotal = solPaid.reduce((t, a) => t + a.pricing!.monthly_price_cents, 0);
  const client = sol.clientSlug ? findClient(sol.clientSlug) : undefined;
  return (
          <div className="hx-sol" id={`sol-panel-${sol.id}`} role="tabpanel" aria-labelledby={`sol-tab-${sol.id}`} hidden={!active}>
            <div className="hx-sol-copy">
              <h3>{sol.title}</h3>
              <p>{sol.desc}</p>
              <ul className="hx-bullets">
                {sol.bullets.map((b) => (
                  <li key={b}>{b}</li>
                ))}
              </ul>
              {client &&
                (() => {
                  const inner = (
                    <>
                      {client.logo ? (
                        <ClientLogo client={client} size={40} />
                      ) : (
                        <span className="hx-logo" aria-hidden>
                          {clientInitials(client.name)}
                        </span>
                      )}
                      <span>
                        Cliente real
                        <strong>
                          {client.name}
                          {client.href ? ' →' : ''}
                        </strong>
                      </span>
                    </>
                  );
                  return client.href ? (
                    <Link className="hx-realclient" to={client.href}>
                      {inner}
                    </Link>
                  ) : (
                    <div className="hx-realclient">{inner}</div>
                  );
                })()}
              {sol.sectorSlug && (
                <Link className="hx-link" to={`/setores/${sol.sectorSlug}`}>
                  Ver a página do setor →
                </Link>
              )}
            </div>
            <div className="hx-pack">
              <span className="hx-label">Apps do pacote</span>
              {solPaid.map((a) => (
                <div className="hx-row" key={a.slug}>
                  <span>
                    <b>{a.name}</b> {sol.notes?.[a.slug] && <small>· {sol.notes[a.slug]}</small>}
                  </span>
                  <span>{formatAppPrice(a.pricing!.monthly_price_cents)}</span>
                </div>
              ))}
              {solFree.length > 0 && (
                <div className="hx-row free">
                  <span>
                    <b>{solFree.map((a) => a.name).join(', ')}</b>
                  </span>
                  <span>Sem custo</span>
                </div>
              )}
              <div className="hx-total">
                <span>Apps do pacote, somados<br /><small>+ o plano base da sua equipe</small></span>
                <strong>{formatPlanPrice(solTotal)}/mês</strong>
              </div>
              <button type="button" className="hx-solid" onClick={() => onAdd(solApps.filter((a) => !isSoon(a)).map((a) => a.slug))}>
                Adicionar pacote ao plano
              </button>
            </div>
            <div className="hx-shot">
              <picture>
                <source srcSet={`/screens/${sol.img}.avif`} type="image/avif" />
                <source srcSet={`/screens/${sol.img}.webp`} type="image/webp" />
                <img src={`/screens/${sol.img}.png`} alt={sol.imgAlt} width={1600} height={1000} loading="lazy" decoding="async" />
              </picture>
            </div>
          </div>
  );
}

/**
 * Soluções por tipo de negócio + Portal de Apps + barra do plano montado.
 * Os três dividem a mesma seleção: "Adicionar pacote ao plano" marca os apps
 * lá embaixo e a barra fixa leva tudo para o checkout.
 */
export function PlanBuilder({ apps }: { apps: PublicApp[] }) {
  const appsTotal = apps.length;
  const freeTotal = apps.filter((a) => a.tier === 'included').length;
  const { sortedPlans } = usePlansCatalog();
  const plans = useMemo(() => buyablePlans(sortedPlans), [sortedPlans]);

  const [tab, setTab] = useState(0);
  const [planCode, setPlanCode] = useState<string | null>(null);
  const [sel, setSel] = useState<Record<string, true>>({});
  const [cat, setCat] = useState('all');
  const [q, setQ] = useState('');
  const appsRef = useRef<HTMLElement>(null);

  const plan = plans.find((p) => p.code === planCode) ?? plans.find((p) => p.is_popular) ?? plans[0];
  const bySlug = useMemo(() => new Map(apps.map((a) => [a.slug, a])), [apps]);


  const toggle = (slug: string) =>
    setSel((s) => {
      const n = { ...s };
      if (n[slug]) delete n[slug];
      else n[slug] = true;
      return n;
    });

  const addPackage = (slugs: string[]) => {
    setSel((s) => {
      const n = { ...s };
      slugs.forEach((slug) => (n[slug] = true));
      return n;
    });
    appsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const qn = norm(q.trim());
  const visible = apps.filter(
    (a) => !qn || norm(`${a.name} ${a.description ?? ''}`).includes(qn),
  );
  const groupOf = (a: PublicApp) => CATEGORY_GROUPS.find((g) => g.cats.includes(a.category));
  const catCounts = (id: string) =>
    visible.filter((a) => id === 'all' || groupOf(a)?.id === id).length;
  const groups = CATEGORY_GROUPS.filter((g) => cat === 'all' || cat === g.id)
    .map((g) => ({ ...g, apps: visible.filter((a) => groupOf(a)?.id === g.id) }))
    .filter((g) => g.apps.length > 0);

  const paidSel = apps.filter((a) => sel[a.slug] && isPaid(a));
  const selCount = Object.keys(sel).length;
  const addonsCents = paidSel.reduce((t, a) => t + a.pricing!.monthly_price_cents, 0);
  const totalCents = plan ? effectivePlanPriceCents(plan) + addonsCents : 0;
  const checkout = plan
    ? calculatorCheckoutHref({
        planCode: plan.code,
        addonSlugs: paidSel.filter((a) => a.addon_code).map((a) => a.slug),
        extraSeats: 0,
        extraChannels: 0,
      })
    : '/assinar';

  return (
    <>
      <section className="hx-sec" id="solucoes">
        <div className="hx-wrap">
          <div className="hx-head rev">
            <span className="hx-label blue">Soluções por operação</span>
            <h2>Um pacote pronto para cada tipo de negócio.</h2>
            <p>
              Escolha a sua operação e veja exatamente quais apps entram e quanto custa. Um clique adiciona o
              pacote ao seu plano.
            </p>
          </div>
          <div className="hx-tabs" role="tablist" aria-label="Tipo de negócio">
            {solutions.map((s, i) => (
              <button
                key={s.id}
                type="button"
                role="tab"
                id={`sol-tab-${s.id}`}
                aria-selected={tab === i}
                aria-controls="sol-panel"
                className="hx-tab"
                onClick={() => setTab(i)}
              >
                {s.label}
              </button>
            ))}
          </div>
          {solutions.map((sol, i) => (
            <SolutionPanel key={sol.id} sol={sol} active={tab === i} bySlug={bySlug} onAdd={addPackage} />
          ))}
        </div>
      </section>

      <section className="hx-sec soft" id="apps" ref={appsRef}>
        <div className="hx-wrap">
          <div className="hx-head rev">
            <span className="hx-label blue">Portal de Apps</span>
            <h2>
              {appsTotal} apps com preço à vista. Monte o seu em 2 passos.
            </h2>
            <p>
              O plano define quantas pessoas atendem. Os apps definem o que o sistema faz.{' '}
              {freeTotal} deles você ativa sem custo.
            </p>
          </div>

          <div className="hx-card">
            <div className="hx-step">
              <b>1</b>
              <div>
                <strong>Escolha o tamanho da equipe</strong>
                <span>Plano mensal com atendimento e funil. Troque quando quiser.</span>
              </div>
            </div>
            <div className="hx-plans">
              {plans.map((p) => (
                <button
                  key={p.code}
                  type="button"
                  className="hx-plan"
                  aria-pressed={plan?.code === p.code}
                  onClick={() => setPlanCode(p.code)}
                >
                  <b>
                    {p.name}
                    <i>{plan?.code === p.code ? '✓' : ''}</i>
                  </b>
                  <span>{formatPlanPrice(effectivePlanPriceCents(p))}/mês</span>
                  <small>
                    {formatPlanSeats(p)} · {formatPlanChannels(p)}
                  </small>
                </button>
              ))}
            </div>
          </div>

          <div className="hx-card">
            <div className="hx-step">
              <b>2</b>
              <div>
                <strong>Adicione os apps da sua operação</strong>
                <span>Cobrados por mês, só enquanto estiverem ativos.</span>
              </div>
            </div>
            <input
              className="hx-search"
              type="search"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Buscar app: estoque, nota fiscal, agenda, marketplace…"
              aria-label="Buscar apps"
            />
            <div className="hx-cats">
              <button type="button" className="hx-cat" aria-pressed={cat === 'all'} onClick={() => setCat('all')}>
                Todos <span>{catCounts('all')}</span>
              </button>
              {CATEGORY_GROUPS.filter((g) => catCounts(g.id) > 0 || cat === g.id).map((g) => (
                <button
                  key={g.id}
                  type="button"
                  className="hx-cat"
                  aria-pressed={cat === g.id}
                  onClick={() => setCat(g.id)}
                >
                  {g.label} <span>{catCounts(g.id)}</span>
                </button>
              ))}
            </div>
            {groups.length === 0 && (
              <div className="hx-empty">
                Nenhum app encontrado para “{q}”.{' '}
                <button type="button" onClick={() => setQ('')}>
                  Limpar busca
                </button>
              </div>
            )}
            {groups.map((g) => (
              <div className="hx-group" key={g.id}>
                <h3>
                  {g.label}
                  <small>
                    {g.apps.length} {g.apps.length === 1 ? 'app' : 'apps'}
                  </small>
                </h3>
                <div className="hx-apps">
                  {g.apps.map((a) => (
                    <AppCard key={a.slug} app={a} on={Boolean(sel[a.slug])} toggle={() => toggle(a.slug)} />
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {selCount > 0 && plan && (
        <div className="hx-bar" role="region" aria-label="Plano montado">
          <div>
            <div className="hx-bar-sum">
              <span>
                Plano {plan.name} + {selCount} {selCount === 1 ? 'app' : 'apps'}
              </span>
              <strong>
                {formatPlanPrice(totalCents)}
                <small>/mês</small>
              </strong>
            </div>
            <button type="button" onClick={() => setSel({})}>
              Limpar
            </button>
            <Link className="btn btn-primary" to={checkout}>
              Assinar agora
            </Link>
          </div>
        </div>
      )}
    </>
  );
}
